import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { Box, useTheme } from '@mui/material';
import { Calendar2 } from 'iconsax-react';
import { HFilledTextField } from '../TextField';
import { useResponsive } from '@ui/style/useResponsive';
import './HDateRangePickerStyles.css';
import InfoIconTooltip from '../Tooltips/InfoIconTooltip';
/** Walk up the DOM and collect all scrollable ancestors */
function getScrollableAncestors(el) {
    const scrollables = [];
    let current = el.parentElement;
    while (current) {
        const { overflow, overflowY } = window.getComputedStyle(current);
        if (/(auto|scroll)/.test(overflow + overflowY)) {
            scrollables.push(current);
        }
        current = current.parentElement;
    }
    return scrollables;
}
const HDateRangePicker = ({ handleSelectedRange, label = '', labelTooltipText, bg = 'light', value, readOnly = false, height = false, portal = false }) => {
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const datePickerRef = useRef(null);
    const containerRef = useRef(null);
    const closeIfOpen = () => {
        const ref = datePickerRef.current;
        if (ref?.isOpen && typeof ref.closeCalendar === 'function') {
            ref.closeCalendar();
        }
    };
    // Close calendar on scroll of any scrollable ancestor
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const ancestors = getScrollableAncestors(el);
        // Also listen on window for safety
        const targets = [...ancestors, window];
        targets.forEach((t) => t.addEventListener('scroll', closeIfOpen, { passive: true }));
        return () => {
            targets.forEach((t) => t.removeEventListener('scroll', closeIfOpen));
        };
    }, []);
    // Also close when the field scrolls out of view (original IntersectionObserver logic kept)
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (!entry.isIntersecting)
                closeIfOpen();
        }, { threshold: 0, rootMargin: '0px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const getDateValues = () => {
        if (value?.startDate && value?.endDate && value.startDate !== '' && value.endDate !== '') {
            try {
                return [new DateObject(new Date(value.startDate)), new DateObject(new Date(value.endDate))];
            }
            catch (error) {
                console.warn('Invalid date format in value:', value);
            }
        }
        return [];
    };
    const handleDateRangeValues = (selectedDates) => {
        if (selectedDates && selectedDates.length === 2) {
            const fromDate = selectedDates[0].toDate();
            const toDate = selectedDates[1].toDate();
            handleSelectedRange({
                startDate: fromDate.toISOString(),
                endDate: toDate.toISOString()
            });
            if (datePickerRef.current) {
                datePickerRef.current.closeCalendar();
            }
        }
    };
    const today = new DateObject();
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, labelTooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: labelTooltipText, size: 'small', variant: 'dark' }) }))] })), [label, labelTooltipText]);
    return (_jsx(Box, { ref: containerRef, sx: { width: '100%' }, children: _jsx(DatePicker, { className: 'h-date-range-picker', ref: datePickerRef, range: true, rangeHover: true, weekStartDayIndex: 1, value: getDateValues(), onChange: handleDateRangeValues, dateSeparator: ' - ', format: 'DD / MM / YYYY', portal: portal, portalTarget: portal && typeof document !== 'undefined' ? document.body : undefined, zIndex: portal ? theme.zIndex.modal + 10 : undefined, fixMainPosition: portal ? true : undefined, fixRelativePosition: portal ? true : undefined, render: (value, openCalendar, handleValueChange) => (_jsx(HFilledTextField, { value: value, label: styledLabel, bg: bg, size: 'medium', fullWidth: true, disabled: readOnly, inputProps: {
                    readOnly: readOnly,
                    style: { cursor: readOnly ? 'not-allowed' : 'pointer' }
                }, onClick: readOnly ? undefined : openCalendar, onChange: readOnly
                    ? undefined
                    : (e) => handleValueChange({
                        target: { value: e.target.value }
                    }), slotProps: {
                    input: {
                        endAdornment: (_jsx(Calendar2, { className: 'end', fontSize: 20, style: { cursor: readOnly ? 'not-allowed' : 'pointer', marginRight: 6 }, color: bg === 'blue' ? theme.palette.action.active : theme.palette.text.primary }))
                    }
                } })), containerStyle: { width: '100%' }, showOtherDays: true, numberOfMonths: isMobile ? 1 : 2 }) }));
};
export default HDateRangePicker;
